import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { FilterPageProps, onlineStoreId } from "pages/filter";
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";

import FilterApi from "@/api/FilterApi";
import ProductApi, { PRODUCTS_PER_PAGE } from "@/api/ProductsApi";
import { AppliedFilters } from "@/components/filter/AppliedFilters";
import { appliedFiltersWithoutStores } from "@/components/filter/appliedFiltersWithoutStores";
import { appliedSearchState } from "@/components/filter/appliedSearchState";
import { appliedStoresState } from "@/components/filter/appliedStoresState";
import { selectedFiltersSelector } from "@/components/filter/selectedFiltersSelector";
import { SubCategoryFilter } from "@/components/filter/SubCategoryFilter";
import { useFilterHandlers } from "@/components/filter/useFilterHandlers";
import { Header } from "@/components/Header";
import { LayoutProducts } from "@/components/LayoutProducts";
import { SecondaryFilterModal } from "@/components/modals/SecondaryFilterModal";
import { Products } from "@/components/Products";
import { SecondaryFilterBtn } from "@/components/SecondaryFilterBtn";
import { GoUpButton } from "@/components/ui-components/GoUpButton";
import { goUpFunctionsState, goUpState } from "@/hooks/useGoUpListener";
import { useToggle } from "@/hooks/useToggle";

import styles from "./FilterPageComponent.module.scss";

const FilterPageComponent = ({ stores, filter, header }: FilterPageProps) => {
  const [isReadyToShow, setIsReadyToShow] = useState(false);
  const selectedFilters = useRecoilValue(selectedFiltersSelector);
  const goUpVisibleState = useRecoilValue(goUpState);
  const goUpFunctions = useRecoilValue(goUpFunctionsState);

  const appliedFilters = useRecoilValue(appliedFiltersWithoutStores);
  const appliedStores = useRecoilValue(appliedStoresState);
  const appliedSearch = useRecoilValue(appliedSearchState);

  const filters = [appliedFilters, appliedStores, appliedSearch];

  const productsQuery = useInfiniteQuery(
    ["products", ...filters],
    ({ pageParam = 0 }) => {
      return ProductApi.all({
        offset: pageParam,
        filter: {
          ...appliedFilters,
          stores: appliedStores,
          search: appliedSearch,
        },
      });
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: isReadyToShow,
      getNextPageParam: (lastPage, allPages) => {
        const itemsLength = lastPage.data.length;
        if (itemsLength === PRODUCTS_PER_PAGE)
          return lastPage.data.length * allPages.length;

        return undefined;
      },
    }
  );

  const productsPages = productsQuery.data?.pages;
  const firstProductsPage =
    (productsQuery.data && productsQuery.data.pages?.[0].data) || [];
  const isProductEmpty =
    (firstProductsPage.length === 0 && productsQuery.isFetched) || false;

  const {
    resetAllExceptStores,
    setInitialFilters,
    checkSecondaryFiltersApplied,
    applyStores,
  } = useFilterHandlers();

  const {
    isOpen: isSecondFilterOpened,
    open: openSecondFilter,
    close: closeSecondFilter,
  } = useToggle();

  useQuery(
    ["filterQuery", selectedFilters],
    () => FilterApi.filter(selectedFilters),
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    resetStoresToOnlineStore();
  }, []);

  useEffect(() => {
    if (isReadyToShow) return;

    const categories = filter?.categories || [];
    const collections = filter?.collections || [];
    const sizes = filter?.sizes || [];
    const colors = filter?.colors || [];
    const discounts = filter?.discounts || [];
    const search = filter?.search || "";

    setInitialFilters({
      categories,
      collections,
      sizes,
      colors,
      discounts,
      stores,
      search,
    });
    setIsReadyToShow(true);
  }, [isReadyToShow]);

  useEffect(() => {
    if (!isReadyToShow) return;
    if (!filter) {
      return setInitialFilters({
        categories: [],
        collections: [],
        sizes: [],
        colors: [],
        discounts: [],
        stores,
        search: "",
      });
    }

    setInitialFilters({ ...filter, stores });
  }, [filter]);

  //@TODO: выпилить со временем этот костыль
  // сбрасываем фильтр магазинов: принудительно ставим онлайн-магазин
  const resetStoresToOnlineStore = () => {
    const storeWasReset =
      window.localStorage.getItem("store_was_reset") === "1";

    if (storeWasReset) return;
    window.localStorage.setItem("store_was_reset", "1");

    if (!stores.includes(onlineStoreId)) {
      applyStores([onlineStoreId]);
      window.location.reload();
    }
  };

  const isGoUpButtonShown = goUpVisibleState.products;
  const goUpHandler = goUpFunctions.products;

  const secondaryFiltersApplied = checkSecondaryFiltersApplied();
  const showFiltersLine = secondaryFiltersApplied || appliedSearch;

  return (
    <LayoutProducts
      stickyContainerSlot={
        <>
          <Header
            className={styles.header}
            isProfileButton
            logoClickHandler={resetAllExceptStores}
          />
          {!showFiltersLine && <SubCategoryFilter />}
          {showFiltersLine && <AppliedFilters />}
        </>
      }
    >
      <div className={styles.contentContainer}>
        <Products
          className={styles.contentTab}
          productsPages={productsPages}
          fetchNextPage={() => productsQuery.fetchNextPage()}
          isProductEmpty={isProductEmpty}
          isBlocksEmpty={true}
          isMainPage={false}
          header={header}
        />
      </div>

      {isGoUpButtonShown && (
        <GoUpButton className={styles.goUp} onClick={goUpHandler} />
      )}

      <SecondaryFilterBtn openFilter={openSecondFilter} />

      <SecondaryFilterModal
        isOpen={isSecondFilterOpened}
        close={closeSecondFilter}
      />
    </LayoutProducts>
  );
};

export default FilterPageComponent;
